import React, { useEffect, useMemo, useState } from "react";
import { Modal, Button } from "react-bootstrap";
import toast from "react-hot-toast";
import dayjs from "dayjs";
import { useBulkCreateAssignmentsMutation } from "../../masters/customers/customersApi";
import { useGetGuardRequirementsForOrderQuery } from "../guardingApi";
import { useGetAreasQuery } from "../../masters/areas/areasApi";
import { linesForRequest } from "../pendingRequest";

// Create the customer's guarding posts straight from a pending deployment
// (2026-09-09). Everything needed is already on the order - customer, area,
// how many guards, which shifts - so this saves retyping it on the Customer
// screen and then having to match it back up by hand.
//
// DAY AND NIGHT ARE ALWAYS SEPARATE POSTS, never one combined row. That is the
// standing rule for every path that creates assignments, and here it falls out
// naturally: each guard requirement LINE already carries its own Shift and Qty,
// so a customer needing 2 day + 1 night is three posts from two lines.
//
// Marking the deployment as Deployed stays a separate step - creating the posts
// is planning, deploying is a fact about the world.
const CreateAssignmentsFromDeploymentModal = ({ show, onClose, order }) => {
  const [bulkCreate, { isLoading }] = useBulkCreateAssignmentsMutation();
  const { data: orderLines = [], isLoading: linesLoading } = useGetGuardRequirementsForOrderQuery(order?.recordGuid, {
    skip: !show || !order?.recordGuid,
  });
  // `order` is a pending deployment REQUEST (Batch161) - posts are raised for
  // that request's lines only (same start date + service), not the whole order.
  const lines = useMemo(() => linesForRequest(orderLines, order), [orderLines, order]);
  const { data: areas = [] } = useGetAreasQuery({ branchId: order?.branchId }, { skip: !show || !order?.branchId });

  const [namePrefix, setNamePrefix] = useState("POST");
  const [area, setArea] = useState("");
  const [effectiveDate, setEffectiveDate] = useState(dayjs().format("YYYY-MM-DD"));

  useEffect(() => {
    if (!show) return;
    setNamePrefix("POST");
    // The order's own area is only a hint - it is free text on the order and may
    // not match the Area master, which is what assignments must be keyed on.
    setArea(areas.some((a) => a.areaName === order?.area) ? order.area : "");
    setEffectiveDate(
      order?.requestedDeploymentDate ? String(order.requestedDeploymentDate).slice(0, 10) : dayjs().format("YYYY-MM-DD")
    );
  }, [show, order, areas]);

  // One group per shift, carrying how many posts that shift needs. Falls back to
  // the order's own guard count as a single Day group when an order somehow has
  // no requirement lines, so the button still does something sensible.
  const shiftGroups = useMemo(() => {
    if (lines.length === 0) {
      return order?.guardsCount > 0 ? [{ shift: "Day", count: order.guardsCount }] : [];
    }
    const byShift = new Map();
    lines.forEach((l) => {
      const shift = l.shift === "NIGHT" ? "Night" : "Day";
      const outstanding = Math.max(0, (l.qty ?? 0) - (l.deployedQty ?? 0));
      if (outstanding > 0) byShift.set(shift, (byShift.get(shift) || 0) + outstanding);
    });
    return [...byShift.entries()].map(([shift, count]) => ({ shift, count }));
  }, [lines, order]);

  const totalPosts = shiftGroups.reduce((sum, g) => sum + g.count, 0);

  const handleCreate = async () => {
    if (!area) return toast.error("Choose the area these posts belong to");
    if (totalPosts === 0) return toast.error("There are no outstanding guards to create posts for");
    try {
      // One call per shift - the bulk endpoint takes a single DayNight, which is
      // exactly the shape that keeps Day and Night as separate posts.
      for (const group of shiftGroups) {
        // eslint-disable-next-line no-await-in-loop
        await bulkCreate({
          branchId: order.branchId,
          custId: order.custId,
          assignmentNamePrefix: `${namePrefix.trim() || "POST"} ${group.shift}`,
          assignmentArea: area,
          dayNight: group.shift,
          count: group.count,
          effectiveDate,
          isTemp: false,
          tempDays: null,
        }).unwrap();
      }
      toast.success(`${totalPosts} guarding post${totalPosts === 1 ? "" : "s"} created`);
      onClose();
    } catch (e) {
      toast.error(e?.message || "Could not create the guarding posts");
    }
  };

  if (!order) return null;

  return (
    <Modal show={show} onHide={isLoading ? undefined : onClose} centered backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title>Create Guarding Posts</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="text-muted small mb-3">
          {order.orderNumber} &middot; {order.customerName} &middot; {order.branchCode}
        </p>

        {order.custId == null ? (
          <div className="alert alert-warning mb-0 py-2 px-3">
            <small>This order is not linked to a customer record, so posts cannot be created from it.</small>
          </div>
        ) : (
          <>
            {linesLoading ? (
              <p className="text-muted">Loading the guard requirement...</p>
            ) : (
              <div className="border rounded p-2 mb-3">
                <div className="fw-semibold mb-1" style={{ fontSize: 13 }}>What will be created</div>
                {shiftGroups.length === 0 ? (
                  <div className="text-muted" style={{ fontSize: 12.5 }}>
                    Every guard on this order is already deployed - nothing outstanding to create.
                  </div>
                ) : (
                  shiftGroups.map((g) => (
                    <div key={g.shift} style={{ fontSize: 12.5 }}>
                      {g.count} &times; <strong>{g.shift}</strong> post{g.count === 1 ? "" : "s"}
                    </div>
                  ))
                )}
                <div className="text-muted mt-1" style={{ fontSize: 11.5 }}>
                  Day and Night are always created as separate posts.
                </div>
              </div>
            )}

            <div className="row g-2">
              <div className="col-md-6">
                <label className="form-label">Post name prefix</label>
                <input
                  type="text"
                  className="form-control"
                  value={namePrefix}
                  onChange={(e) => setNamePrefix(e.target.value)}
                />
                <small className="text-muted">Numbered per shift, e.g. "{(namePrefix || "POST").trim()} Day 1".</small>
              </div>
              <div className="col-md-6">
                <label className="form-label">Area <span className="text-danger">*</span></label>
                {/* From the Area master only - supervisor-area assignment and the
                    attendance dashboard both match this by name, so free text
                    would drop the posts out of both. */}
                <select className="form-control" value={area} onChange={(e) => setArea(e.target.value)}>
                  <option value="">--Select--</option>
                  {areas.map((a) => (
                    <option key={a.areaId} value={a.areaName}>{a.areaName}</option>
                  ))}
                </select>
                {order.area && !areas.some((a) => a.areaName === order.area) && (
                  <small className="text-warning">
                    The order says "{order.area}", which is not in the Area master - pick the real one.
                  </small>
                )}
              </div>
              <div className="col-md-6">
                <label className="form-label">Effective from</label>
                <input
                  type="date"
                  className="form-control"
                  value={effectiveDate}
                  onChange={(e) => setEffectiveDate(e.target.value)}
                />
              </div>
            </div>
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onClose} disabled={isLoading}>Cancel</Button>
        <Button
          variant="primary"
          onClick={handleCreate}
          disabled={isLoading || order.custId == null || totalPosts === 0}
        >
          {isLoading ? "Creating..." : `Create ${totalPosts || ""} Post${totalPosts === 1 ? "" : "s"}`}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default CreateAssignmentsFromDeploymentModal;
